import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { UtilsService } from 'app/helpers/services';
import { CompRouterService } from 'app/helpers/services/core/comp-router.service';

interface AnimatedEmoji {
  value: number;
  emoji: string;
  animatedEmoji?: string;
  selected: boolean;
  interactable: boolean;
  label: string;
  isAnimating?: boolean;
}

@Component({
  selector: 'app-time-card',
  templateUrl: './time-card.component.html',
  styleUrls: ['./time-card.component.scss']
})
export class TimeCardComponent implements OnInit {

  @Output() formSubmitted: EventEmitter<any> = new EventEmitter();

  rating: any;
  emojis: AnimatedEmoji[] = [
    { value: 1, emoji: '😞', animatedEmoji: '😢', selected: false, interactable: false, label: 'Bad', isAnimating: false },
    { value: 2, emoji: '🙁', animatedEmoji: '😟', selected: false, interactable: false, label: 'Not so good', isAnimating: false },
    { value: 3, emoji: '😐', animatedEmoji: '😑', selected: false, interactable: false, label: 'Okay', isAnimating: false },
    { value: 4, emoji: '🙂', animatedEmoji: '😊', selected: false, interactable: false, label: 'Good', isAnimating: false },
    { value: 5, emoji: '😊', animatedEmoji: '🤩', selected: false, interactable: false, label: 'Excellent', isAnimating: false }
  ];

  // Animation control properties
  private animationTimeouts: { [key: number]: any } = {};
  private pulseAnimationActive = false;
  private currentPulseIndex = 0;
  private pulseInterval: any;

  constructor(public utils: UtilsService, public compRouter: CompRouterService) { }

  ngOnInit() { 
    this.startPulseAnimation();
  }

  get feedbackTitle(): string {
    const today = new Date();
    const isMonday = today.getDay() === 1; // 0 = Sunday, 1 = Monday, etc.
    return isMonday ? 'How was your past week?' : 'How was your week?';
  }

  goToDetailedAnalytics() {
    this.compRouter.routeToContextUrls('mood-of-the-crew-analytics');
  }

  onEmojiClick(rating: number) {
    this.rating = null;
    setTimeout(() => this.rating = rating);
  }

  onFormSubmitted() {
    // Hide the time-card component when the form is successfully submitted
    this.formSubmitted.emit();
  }

  // Animation methods
  private startPulseAnimation() {
    if (this.pulseInterval) {             
      clearInterval(this.pulseInterval);  
    }

    if (!this.pulseAnimationActive) {
      this.pulseAnimationActive = true;

      this.pulseInterval = setInterval(() => {
        if ((this.rating === undefined || this.rating === null) && !this.isFeedbackModalActive()) {
          const emoji = this.emojis[this.currentPulseIndex];

          if (!emoji.selected && !emoji.isAnimating) {
            this.triggerEmojiAnimation(emoji, 'pulse');
          }
          this.currentPulseIndex = (this.currentPulseIndex + 1) % this.emojis.length;
        } else {
          this.pulseAnimationActive = false;
        }
      }, 1800); 
    }
  }

  private triggerEmojiAnimation(emoji: AnimatedEmoji, animationType: 'pulse' | 'bounce' | 'flip') {
    if (emoji.isAnimating) return;
    emoji.isAnimating = true;

    // Clear any existing timeout for this emoji
    if (this.animationTimeouts[emoji.value]) {
      clearTimeout(this.animationTimeouts[emoji.value]);
    }

    // Set animation timeout
    this.animationTimeouts[emoji.value] = setTimeout(() => {
      emoji.isAnimating = false;
    }, 600); // Animation duration
  }

  getCurrentEmoji(emoji: AnimatedEmoji): string {
    if (emoji.selected && emoji.animatedEmoji) {
      return emoji.animatedEmoji;
    }
    return emoji.emoji;
  }

  getEmojiAnimationClass(emoji: AnimatedEmoji): string {
    let classes = '';

    if (emoji.isAnimating) {
      classes += 'emoji-pulse ';
    }

    return classes.trim();
  }

  getLabelColorClass(emojiValue: number): string {
    const colorMap: { [key: number]: string } = {
      1: 'rating-bad',
      2: 'rating-not-so-good',
      3: 'rating-okay',
      4: 'rating-good',
      5: 'rating-excellent'
    };
    return colorMap[emojiValue] || '';
  }

  private isFeedbackModalActive(): boolean {
    const modalElement = document.getElementById('feedbackModal');
    const isActive = modalElement ? modalElement.classList.contains('in') : false;  
    return isActive;
  }

  ngOnDestroy() {
    // Clear main pulse interval
    clearInterval(this.pulseInterval);
    
    // Reset animation flag
    this.pulseAnimationActive = false;
    
    // Clear individual timeouts
    for (const key in this.animationTimeouts) {
      clearTimeout(this.animationTimeouts[key]);
    }
    
    // Reset objects
    this.animationTimeouts = {};
  }
}
